import React, { useMemo, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import plusIcon from '../../../assets/img/plusIconFilled.svg';
import '../../../assets/css/componentStyles/CommonDropzone.css';

function CommonDropzone({
  onDrop,
  accept,
  disabled,
  dropzoneText,
  maxSize,
  multiple,
  className,
  height,
  width,
  onError,
  allowedExtensions,
  maxFiles,
}) {
  const acceptProp = useMemo(() => {
    if (typeof accept !== 'string' || !accept) {
      return undefined;
    }
    const acceptObj = {};
    accept.split(',').forEach((type) => {
      const trimmedType = type.trim();
      if (trimmedType) {
        acceptObj[trimmedType] = allowedExtensions || [];
      }
    });
    return acceptObj;
  }, [accept, allowedExtensions]);

  const handleDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0 && onError) {
        const errors = rejectedFiles.map((file) => ({
          file: file.file,
          errors: file.errors,
        }));
        onError(errors);
      }

      if (acceptedFiles.length > 0) {
        onDrop(acceptedFiles);
      }
    },
    [onDrop, onError],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    isDragAccept,
    fileRejections,
  } = useDropzone({
    onDrop: handleDrop,
    accept: acceptProp,
    disabled,
    maxSize,
    multiple,
    maxFiles,
  });

  const dropzoneClassName = classNames('dropzone', className, {
    'dropzone--active': isDragActive,
    'dropzone--accept': isDragAccept,
    'dropzone--reject': isDragReject,
    'dropzone--disabled': disabled,
  });

  const dropzoneStyle = {
    ...(height && { height }),
    ...(width && { width }),
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className="dropzone-wrapper">
      <div
        {...getRootProps({
          className: dropzoneClassName,
          style: dropzoneStyle,
        })}
      >
        <input {...getInputProps()} />
        <div className="dropzone-icon-wrapper">
          <img src={plusIcon} alt="" aria-hidden="true" />
        </div>
        <p className="dropzone-text dropzone-text--primary">CLICK TO BROWSE</p>
        <p className="dropzone-text dropzone-text--secondary">{dropzoneText}</p>
        {maxSize && (
          <p className="dropzone-text dropzone-text--info">
            Max file size: {formatFileSize(maxSize)}
          </p>
        )}
        {maxFiles && (
          <p className="dropzone-text dropzone-text--info">Max files: {maxFiles}</p>
        )}
        {isDragActive && (
          <div className="dropzone-overlay">
            {isDragAccept ? (
              <p className="dropzone-overlay-text dropzone-overlay-text--accept">
                Drop files here
              </p>
            ) : (
              <p className="dropzone-overlay-text dropzone-overlay-text--reject">
                Some files are not supported
              </p>
            )}
          </div>
        )}
      </div>
      {fileRejections.length > 0 && (
        <div className="dropzone-errors" role="alert" aria-live="polite">
          <h4 className="dropzone-errors-title">Upload Errors:</h4>
          <ul className="dropzone-errors-list">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.path || file.name} className="dropzone-error-item">
                <span className="dropzone-error-filename">{file.name}</span>
                <ul className="dropzone-error-details">
                  {errors.map((error) => (
                    <li key={error.code} className="dropzone-error-detail">
                      {error.message}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

CommonDropzone.propTypes = {
  onDrop: PropTypes.func.isRequired,
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  dropzoneText: PropTypes.string,
  maxSize: PropTypes.number,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onError: PropTypes.func,
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  maxFiles: PropTypes.number,
};

CommonDropzone.defaultProps = {
  accept: '',
  disabled: false,
  dropzoneText: 'OR DRAG & DROP PHOTOS HERE',
  maxSize: undefined,
  multiple: true,
  className: '',
  height: undefined,
  width: undefined,
  onError: undefined,
  allowedExtensions: undefined,
  maxFiles: undefined,
};

export default CommonDropzone;
